<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import schedulingAPI from '../../scheduling/api/scheduling';
import AppointmentModal from '../../scheduling/components/AppointmentModal.vue';

const props = defineProps({
  contact: {
    type: Object,
    required: true,
  },
  accountId: {
    type: [Number, String],
    default: null,
  },
});

const store = useStore();
const currentAccountId = computed(() => props.accountId || store.getters.getCurrentAccountId);

const patientInfo = ref(null);
const isLoading = ref(false);

// Modal de Vínculo de Familiar
const showLinkModal = ref(false);
const allAvailablePatients = ref([]);
const selectedPatientId = ref('');
const patientSearchQuery = ref('');
const isPatientDropdownOpen = ref(false);
const relationshipToLink = ref('Filha');
const isSubmittingLink = ref(false);

const normalizeText = str => {
  return (str || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
};

const filteredPatientsToLink = computed(() => {
  const query = normalizeText(patientSearchQuery.value);
  if (!query) return allAvailablePatients.value.slice(0, 20);

  return allAvailablePatients.value.filter(p => {
    const nameNorm = normalizeText(p.name);
    const phoneNorm = (p.phone_number || '').replace(/\D/g, '');
    const queryDigits = query.replace(/\D/g, '');

    const matchesName = nameNorm.includes(query);
    const matchesPhone = queryDigits.length >= 2 && phoneNorm.includes(queryDigits);
    return matchesName || matchesPhone;
  });
});

const selectPatientToLink = p => {
  selectedPatientId.value = p.id;
  patientSearchQuery.value = p.name;
  isPatientDropdownOpen.value = false;
};

const clearPatientSelection = () => {
  selectedPatientId.value = '';
  patientSearchQuery.value = '';
  isPatientDropdownOpen.value = true;
};

// Modal de Cadastro de Atendimento / Agendamento (Oficial da Clínica)
const showAppointmentModal = ref(false);
const schedulingServices = ref([]);
const schedulingProfessionals = ref([]);
const schedulingPatients = ref([]);
const schedulingPartners = ref([]);
const initialAppointmentData = ref(null);
const isLoadingSchedulingData = ref(false);

const fetchPatientInfo = async () => {
  if (!props.contact?.id) return;
  isLoading.value = true;
  try {
    const res = await schedulingAPI.lookupPatientForContact(props.contact.id);
    const data = res.data || {};
    if (data.found) {
      patientInfo.value = data.patient_info || {
        patient: data.patient,
        is_primary: data.is_primary,
        relationship: data.relationship,
      };
    } else {
      patientInfo.value = null;
    }
  } catch (error) {
    patientInfo.value = null;
  } finally {
    isLoading.value = false;
  }
};

const openLinkModal = async () => {
  showLinkModal.value = true;
  selectedPatientId.value = '';
  patientSearchQuery.value = '';
  isPatientDropdownOpen.value = true;
  relationshipToLink.value = 'Filha';
  try {
    const res = await schedulingAPI.getPatients();
    allAvailablePatients.value = Array.isArray(res.data) ? res.data : (res.data?.patients || []);
  } catch (err) {
    useAlert('Erro ao carregar lista de pacientes.');
  }
};

const handleConfirmLink = async () => {
  if (!selectedPatientId.value) return;
  isSubmittingLink.value = true;
  try {
    await schedulingAPI.addSecondaryContact(selectedPatientId.value, {
      phone: props.contact.phone_number,
      name: props.contact.name,
      relationship: relationshipToLink.value,
      contact_id: props.contact.id,
    });
    useAlert('Familiar vinculado ao paciente com sucesso!');
    showLinkModal.value = false;
    await fetchPatientInfo();
  } catch (err) {
    useAlert('Erro ao vincular familiar ao paciente.');
  } finally {
    isSubmittingLink.value = false;
  }
};

const handleRemoveLink = async () => {
  if (!patientInfo.value?.patient?.id) return;
  if (!confirm(`Deseja desvincular ${props.contact.name} do paciente ${patientInfo.value.patient.name}?`)) return;

  try {
    await schedulingAPI.removeSecondaryContact(
      patientInfo.value.patient.id,
      props.contact.phone_number
    );
    useAlert('Vínculo removido com sucesso.');
    await fetchPatientInfo();
  } catch (err) {
    useAlert('Erro ao remover vínculo.');
  }
};

const openNewAppointmentModal = async () => {
  if (!props.contact?.id) return;
  isLoadingSchedulingData.value = true;

  try {
    const [srvRes, profRes, patRes, partRes] = await Promise.all([
      schedulingAPI.getServices().catch(() => ({ data: [] })),
      schedulingAPI.getProfessionals().catch(() => ({ data: [] })),
      schedulingAPI.getPatients().catch(() => ({ data: [] })),
      schedulingAPI.getPartners().catch(() => ({ data: [] })),
    ]);

    schedulingServices.value = Array.isArray(srvRes.data) ? srvRes.data : (srvRes.data?.services || []);
    schedulingProfessionals.value = Array.isArray(profRes.data) ? profRes.data : (profRes.data?.professionals || []);
    schedulingPatients.value = Array.isArray(patRes.data) ? patRes.data : (patRes.data?.patients || []);
    schedulingPartners.value = Array.isArray(partRes.data) ? partRes.data : (partRes.data?.partners || []);

    const targetPatient = patientInfo.value?.patient;
    const targetName = targetPatient?.name || props.contact.name;
    const targetPhone = targetPatient?.phone_number || props.contact.phone_number;

    initialAppointmentData.value = {
      scheduling_patient_id: targetPatient?.id || null,
      patient_name: targetName,
      patient_phone: targetPhone,
      patient_email: props.contact.email || '',
      contact_id: props.contact.id,
      session_type: 'single',
      total_sessions: 10,
    };

    showAppointmentModal.value = true;
  } catch (err) {
    useAlert('Erro ao carregar dados para o atendimento.');
  } finally {
    isLoadingSchedulingData.value = false;
  }
};

const onAppointmentSaved = () => {
  showAppointmentModal.value = false;
  useAlert('Atendimento cadastrado com sucesso! Lead sincronizado no CRM.');
  fetchPatientInfo();
};

watch(
  () => props.contact?.id,
  newId => {
    if (newId) {
      fetchPatientInfo();
    }
  },
  { immediate: true }
);

onMounted(() => {
  fetchPatientInfo();
});
</script>

<template>
  <div class="w-full my-2">
    <!-- Loading State -->
    <div v-if="isLoading" class="py-2 flex items-center justify-center text-xs text-slate-500">
      <span class="animate-spin mr-2">⏳</span> Verificando prontuário...
    </div>

    <!-- 1. CASO: PACIENTE PRINCIPAL -->
    <div
      v-else-if="patientInfo && patientInfo.is_primary"
      class="p-3 rounded-xl bg-emerald-950/30 border border-emerald-500/30 text-emerald-100 shadow-xs space-y-2"
    >
      <div class="flex items-center justify-between">
        <span class="flex items-center gap-1.5 text-xs font-bold text-emerald-400">
          <span>🧑‍⚕️</span>
          <span>Paciente Clínico</span>
        </span>
        <span
          v-if="(patientInfo.patient.weekly_frequency || patientInfo.patient.active_enrollments_count || 0) > 0"
          class="text-[10px] font-mono px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 font-bold border border-emerald-500/30"
        >
          {{ patientInfo.patient.weekly_frequency || patientInfo.patient.active_enrollments_count }}x por semana
        </span>
      </div>
      <p class="text-xs text-slate-300">
        Prontuário vinculado a <strong class="text-white">{{ patientInfo.patient.name }}</strong>
      </p>
      <div class="flex items-center justify-between text-[11px] text-slate-400 pt-2 border-t border-emerald-900/50">
        <span>Sessões: <strong class="text-emerald-300 font-mono">{{ patientInfo.patient.completed_sessions || 0 }} de {{ patientInfo.patient.total_sessions || 0 }}</strong></span>
        <button
          type="button"
          class="text-emerald-400 hover:text-emerald-300 font-bold hover:underline cursor-pointer flex items-center gap-1"
          :disabled="isLoadingSchedulingData"
          @click="openNewAppointmentModal"
        >
          <span>📅 Agendar Atendimento</span>
        </button>
      </div>
    </div>

    <!-- 2. CASO: FAMILIAR / RESPONSÁVEL VINCULADO -->
    <div
      v-else-if="patientInfo && !patientInfo.is_primary"
      class="p-3 rounded-xl bg-purple-950/30 border border-purple-500/30 text-purple-100 shadow-xs space-y-2"
    >
      <div class="flex items-center justify-between">
        <span class="flex items-center gap-1.5 text-xs font-bold text-purple-300">
          <span>👨‍👩‍👧</span>
          <span>{{ patientInfo.relationship || 'Familiar' }}</span>
        </span>
        <button
          type="button"
          class="text-[10px] text-rose-400 hover:text-rose-300 underline cursor-pointer"
          title="Desvincular familiar"
          @click="handleRemoveLink"
        >
          Desvincular
        </button>
      </div>
      <p class="text-xs text-slate-300">
        Responsável pelo paciente: <strong class="text-white">{{ patientInfo.patient.name }}</strong>
      </p>
      <div class="flex items-center justify-between text-[11px] text-slate-400 pt-2 border-t border-purple-900/50">
        <span>Sessões do paciente: <strong class="text-purple-300 font-mono">{{ patientInfo.patient.remaining_sessions || 0 }}</strong></span>
        <button
          type="button"
          class="text-purple-400 hover:text-purple-300 font-bold hover:underline cursor-pointer flex items-center gap-1"
          :disabled="isLoadingSchedulingData"
          @click="openNewAppointmentModal"
        >
          <span>📅 Agendar para Paciente</span>
        </button>
      </div>
    </div>

    <!-- 3. CASO: NÃO VINCULADO (Botões de Ação Rápida) -->
    <div
      v-else
      class="p-3 rounded-xl bg-slate-900/50 border border-slate-700/60 text-slate-300 space-y-2"
    >
      <div class="flex items-center gap-1.5 text-[11px] font-bold text-slate-400 uppercase tracking-wide">
        <span>🏥</span>
        <span>Integração com Clínica</span>
      </div>
      <p class="text-[11px] text-slate-400 leading-snug">
        Cadastre um atendimento ou associe o número ao paciente da clínica.
      </p>
      <div class="flex items-center gap-2 pt-1">
        <button
          type="button"
          class="flex-1 py-1.5 px-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-[11px] font-bold flex items-center justify-center gap-1 shadow-sm transition-colors cursor-pointer"
          :disabled="isLoadingSchedulingData"
          @click="openNewAppointmentModal"
        >
          <span>📅</span>
          <span>{{ isLoadingSchedulingData ? 'Carregando...' : 'Novo Atendimento' }}</span>
        </button>
        <button
          type="button"
          class="py-1.5 px-2.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-cyan-300 border border-cyan-500/30 text-[11px] font-semibold flex items-center justify-center gap-1 transition-colors cursor-pointer"
          title="Vincular como familiar de paciente existente"
          @click="openLinkModal"
        >
          <span>🔗 Vincular</span>
        </button>
      </div>
    </div>

    <!-- MODAL: Cadastro de Atendimento / Agendamento Oficial -->
    <AppointmentModal
      v-if="showAppointmentModal"
      :services="schedulingServices"
      :professionals="schedulingProfessionals"
      :patients="schedulingPatients"
      :partners="schedulingPartners"
      :initial-data="initialAppointmentData"
      @close="showAppointmentModal = false"
      @saved="onAppointmentSaved"
    />

    <!-- MODAL: Vincular Familiar a Paciente Existente -->
    <div
      v-if="showLinkModal"
      class="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-xs"
    >
      <div class="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-md p-6 shadow-2xl space-y-4 text-left">
        <div class="flex items-center justify-between">
          <h3 class="text-base font-bold text-white flex items-center gap-2">
            <span>🔗</span>
            <span>Vincular a Paciente Existente</span>
          </h3>
          <button
            type="button"
            class="text-slate-400 hover:text-white text-lg p-1 cursor-pointer"
            @click="showLinkModal = false"
          >
            ✕
          </button>
        </div>

        <p class="text-xs text-slate-300 leading-relaxed">
          Associe o contato atual (<strong class="text-white">{{ contact.name }} - {{ contact.phone_number }}</strong>) a um paciente existente na clínica.
        </p>

        <div class="space-y-3">
          <div class="relative">
            <label class="block text-[11px] font-bold text-slate-300 uppercase tracking-wider mb-1">
              Buscar Paciente da Clínica (Nome ou WhatsApp)
            </label>
            <div class="relative">
              <input
                v-model="patientSearchQuery"
                type="text"
                class="w-full h-10 pl-9 pr-8 text-xs bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500"
                placeholder="Digite o nome ou telefone do paciente..."
                @focus="isPatientDropdownOpen = true"
                @input="isPatientDropdownOpen = true"
              />
              <span class="absolute left-3 top-2.5 text-slate-400 text-xs">🔍</span>
              <button
                v-if="patientSearchQuery || selectedPatientId"
                type="button"
                class="absolute right-2.5 top-2.5 text-slate-400 hover:text-white text-xs cursor-pointer p-0.5"
                title="Limpar seleção"
                @click="clearPatientSelection"
              >
                ✕
              </button>
            </div>

            <!-- Dropdown Flutuante de Pacientes Encontrados -->
            <div
              v-if="isPatientDropdownOpen"
              class="absolute left-0 right-0 top-full mt-1 max-h-48 overflow-y-auto bg-slate-800 border border-slate-700 rounded-xl shadow-2xl z-50 divide-y divide-slate-700/50"
            >
              <div
                v-if="filteredPatientsToLink.length === 0"
                class="px-3 py-3 text-center text-xs text-slate-400"
              >
                Nenhum paciente encontrado com esse nome.
              </div>
              <button
                v-for="p in filteredPatientsToLink"
                :key="p.id"
                type="button"
                class="w-full px-3 py-2.5 text-left text-xs flex items-center justify-between hover:bg-slate-700 transition-colors cursor-pointer"
                :class="selectedPatientId === p.id ? 'bg-cyan-950/40 text-cyan-300 font-bold' : 'text-slate-200'"
                @click="selectPatientToLink(p)"
              >
                <div class="flex flex-col min-w-0 pr-2">
                  <span class="truncate">{{ p.name }}</span>
                  <span class="text-[10px] text-slate-400 font-mono">{{ p.phone_number || 'Sem telefone' }}</span>
                </div>
                <span v-if="selectedPatientId === p.id" class="text-cyan-400 font-bold shrink-0">✓ Selecionado</span>
              </button>
            </div>
          </div>

          <div>
            <label class="block text-[11px] font-bold text-slate-300 uppercase tracking-wider mb-1">
              Grau de Parentesco / Relação
            </label>
            <select
              v-model="relationshipToLink"
              class="w-full h-10 px-3 text-xs bg-slate-800 border border-slate-700 rounded-xl text-white focus:outline-none focus:border-cyan-500 cursor-pointer"
            >
              <option value="Filha">Filha</option>
              <option value="Filho">Filho</option>
              <option value="Mãe">Mãe</option>
              <option value="Pai">Pai</option>
              <option value="Esposa">Esposa</option>
              <option value="Esposo">Esposo</option>
              <option value="Responsável Legal">Responsável Legal</option>
              <option value="Secretária / Cuidador">Secretária / Cuidador</option>
              <option value="Outro">Outro Familiar</option>
            </select>
          </div>
        </div>

        <div class="flex items-center justify-end gap-2 pt-2 border-t border-slate-800">
          <button
            type="button"
            class="px-4 py-2 text-xs font-semibold text-slate-300 hover:text-white bg-slate-800 hover:bg-slate-700 rounded-xl transition-colors cursor-pointer"
            @click="showLinkModal = false"
          >
            Cancelar
          </button>
          <button
            type="button"
            class="px-4 py-2 text-xs font-bold text-white bg-cyan-600 hover:bg-cyan-500 rounded-xl transition-colors flex items-center gap-1.5 shadow-sm disabled:opacity-50 cursor-pointer"
            :disabled="!selectedPatientId || isSubmittingLink"
            @click="handleConfirmLink"
          >
            <span>{{ isSubmittingLink ? 'Vinculando...' : 'Confirmar Vínculo' }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
